import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faBroom,
    faArrowsRotate,
    faPaperPlane,
    faComments,
    faRobot,
    faUser,
    faLayerGroup,
    faTag,
    faCircleInfo,
    faXmark,
    faClock,
    faArrowUpRightFromSquare
} from '@fortawesome/free-solid-svg-icons';
import Header from './header';
import Footer from './footer';
import { hasProductPermission } from '../utils/permissions';
import '../insightStudio.css';

/**
 * A 304 from a conditional request reaches fetch() as ok === false with an empty
 * body, which would send every loader below down its failure path on a cache
 * hit. These payloads are small and must be current, so skip the HTTP cache.
 */
const NO_STORE = { cache: 'no-store' };

/**
 * Trace a chat exchange in the browser console.
 *
 * Every entry is collapsed and prefixed the same way, so filtering the console
 * on "Insight Studio" shows the whole conversation with the server and nothing
 * else: what was sent to /ask, what came back, how long it took, and the
 * reconciled messages that follow.
 */
const logExchange = (direction, label, payload) => {
    const arrow = direction === 'out' ? '\u2192' : direction === 'in' ? '\u2190' : '\u2715';
    const colour = direction === 'out' ? '#2b97f0' : direction === 'in' ? '#3fb950' : '#f85149';
    console.groupCollapsed(
        `%c[Insight Studio] ${arrow} ${label}`,
        `color:${colour};font-weight:600`
    );
    console.log(payload);
    console.groupEnd();
};

const getClientId = () => {
    const raw = sessionStorage.getItem('userEmail');
    try {
        return raw ? JSON.parse(raw) : '';
    } catch {
        return raw || '';
    }
};



const sidebarButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    padding: '8px 10px',
    backgroundColor: '#2a2a2a',
    color: '#fff',
    border: '1px solid #3d3d3d',
    borderRadius: '7px',
    cursor: 'pointer',
    fontSize: '12.5px',
    fontWeight: '600',
    transition: 'background-color 0.15s ease',
    boxSizing: 'border-box'
};

const selectStyle = {
    width: '100%',
    padding: '8px 10px',
    backgroundColor: '#232323',
    color: '#fff',
    border: '1px solid #3d3d3d',
    borderRadius: '7px',
    fontSize: '13px',
    boxSizing: 'border-box'
};

const SearchableSelect = ({ options, value, onChange, getKey, getLabel, placeholder, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wrapperRef = useRef(null);

    const selected = options.find((o) => String(getKey(o)) === String(value));
    const filtered = query.trim()
        ? options.filter((o) => getLabel(o).toLowerCase().includes(query.trim().toLowerCase()))
        : options;

    const commit = (option) => {
        onChange(String(getKey(option)));
        setQuery('');
        setIsOpen(false);
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative' }}>
            <input
                type="text"
                className="insight-select"
                value={isOpen ? query : (selected ? getLabel(selected) : '')}
                onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
                onFocus={(e) => { setIsOpen(true); setQuery(''); e.target.select(); }}
                onBlur={() => setIsOpen(false)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setIsOpen(false); e.target.blur(); } }}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete="off"
                style={{ ...selectStyle, cursor: disabled ? 'not-allowed' : 'text' }}
            />
            {isOpen && !disabled && (
                <div
                    className="insight-scroll"
                    onMouseDown={(e) => e.preventDefault()}
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        maxHeight: '220px',
                        overflowY: 'auto',
                        backgroundColor: '#232323',
                        border: '1px solid #3d3d3d',
                        borderRadius: '7px',
                        zIndex: 20,
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.4)'
                    }}
                >
                    {filtered.length === 0 ? (
                        <div style={{ padding: '9px 10px', color: '#888', fontSize: '12.5px' }}>No matches</div>
                    ) : (
                        filtered.map((option) => {
                            const isActive = String(getKey(option)) === String(value);
                            return (
                                <div
                                    key={getKey(option)}
                                    onClick={() => commit(option)}
                                    style={{
                                        padding: '9px 10px',
                                        fontSize: '12.5px',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        backgroundColor: isActive ? 'rgba(43, 151, 240, 0.15)' : 'transparent'
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isActive ? 'rgba(43, 151, 240, 0.15)' : '#2a2a2a')}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isActive ? 'rgba(43, 151, 240, 0.15)' : 'transparent')}
                                >
                                    {getLabel(option)}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

const Tag = ({ icon, children }) => (
    <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        fontSize: '11px',
        color: '#aaa',
        backgroundColor: '#252525',
        border: '1px solid #3a3a3a',
        borderRadius: '20px',
        padding: '3px 10px'
    }}>
        <FontAwesomeIcon icon={icon} style={{ fontSize: '10px' }} />
        {children}
    </span>
);

// The X logo, inline: @fortawesome/free-brands-svg-icons is not a dependency
// of this project, so faXTwitter is not importable.
const XLogo = ({ size = 13 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
);

/** @BullsNBearsWA from https://twitter.com/BullsNBearsWA/status/123 */
const getTweetHandle = (url) => {
    if (!url) return null;
    const match = /(?:twitter|x)\.com\/([^/?#]+)/i.exec(url);
    return match && match[1] !== 'i' ? match[1] : null;
};

/** Highlight $TICKERS, @mentions and links the way X does. */
const renderTweetText = (text) => {
    if (!text) return null;
    return String(text)
        .split(/(\$[A-Za-z][A-Za-z0-9._-]*|@[A-Za-z0-9_]+|https?:\/\/\S+)/g)
        .filter((part) => part !== '' && part !== undefined)
        .map((part, i) => {
            if (/^https?:\/\//.test(part)) {
                return (
                    <a
                        key={i}
                        href={part}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: '#2b97f0', textDecoration: 'none' }}
                    >
                        {part.length > 42 ? `${part.slice(0, 42)}…` : part}
                    </a>
                );
            }
            if (/^[$@]/.test(part)) {
                return <span key={i} style={{ color: '#2b97f0' }}>{part}</span>;
            }
            return <React.Fragment key={i}>{part}</React.Fragment>;
        });
};

/**
 * Render a timestamp in US Eastern time.
 *
 * The API returns UTC (every value ends in "Z"). Rendering with a bare
 * toLocaleString() would show the viewer's own timezone, so the same tweet read
 * differently depending on who opened the page. Pinning to America/New_York
 * keeps it consistent, and timeZoneName prints EST or EDT so daylight saving is
 * never ambiguous.
 */
const EASTERN_TIME_ZONE = 'America/New_York';

const formatEastern = (value, { withDate = true } = {}) => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date)) return String(value);
    return date.toLocaleString('en-US', {
        timeZone: EASTERN_TIME_ZONE,
        ...(withDate ? { year: 'numeric', month: '2-digit', day: '2-digit' } : {}),
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
    });
};

/**
 * Collapse tweets that carry the same text.
 *
 * A press release is typically reposted verbatim by several accounts, so one
 * story can occupy most of a retrieval. Keeps the first of each and records how
 * many identical ones it stands for.
 */
const tweetFingerprint = (content) =>
    String(content || '')
        .replace(/https?:\/\/\S+/g, '')
        .replace(/[^a-z0-9 ]/gi, ' ')
        .toLowerCase()
        .split(/\s+/)
        .join(' ')
        .slice(0, 120);

const dedupeTweets = (tweets) => {
    const seen = new Map();
    const rows = [];
    (Array.isArray(tweets) ? tweets : []).forEach((tweet) => {
        const key = tweetFingerprint(tweet.content);
        if (seen.has(key)) {
            rows[seen.get(key)].duplicates += 1;
        } else {
            seen.set(key, rows.length);
            rows.push({ tweet, duplicates: 0 });
        }
    });
    return rows;
};

/**
 * How many tweets the details drawer shows before "Load more".
 *
 * A retrieval runs to dozens of tweets; opening the drawer on all of them
 * pushes the answer's own metadata off the top. Ten fills the panel without
 * burying it, and the rest are a click away.
 */
const TWEET_PAGE_SIZE = 10;

const TweetCard = ({ tweet, duplicates = 0 }) => {
    const handle = getTweetHandle(tweet.constructed_url);
    const postedLabel = formatEastern(tweet.date_posted_text);

    return (
        <div style={{
            backgroundColor: '#16181c',
            border: '1px solid #2f3336',
            borderRadius: '12px',
            padding: '11px 13px'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' }}>
                <div style={{
                    flexShrink: 0,
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    backgroundColor: '#000',
                    border: '1px solid #2f3336',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <XLogo size={12} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ color: '#e7e9ea', fontSize: '12.5px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {handle ? `@${handle}` : 'Source tweet'}
                    </div>
                    {postedLabel && (
                        <div style={{ color: '#71767b', fontSize: '10.5px' }}>{postedLabel}</div>
                    )}
                </div>
                {duplicates > 0 && (
                    <span
                        title={`This text was posted by ${duplicates + 1} accounts; the rest are collapsed.`}
                        style={{
                            flexShrink: 0,
                            fontSize: '10px',
                            color: '#71767b',
                            border: '1px solid #2f3336',
                            borderRadius: '20px',
                            padding: '1px 7px'
                        }}
                    >
                        +{duplicates} reposts
                    </span>
                )}
                {tweet.constructed_url && (
                    <a
                        href={tweet.constructed_url}
                        target="_blank"
                        rel="noreferrer"
                        title="Open on X"
                        style={{ color: '#71767b', flexShrink: 0, fontSize: '11px' }}
                    >
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </a>
                )}
            </div>
            <div style={{
                color: '#e7e9ea',
                fontSize: '13px',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
            }}>
                {renderTweetText(tweet.content)}
            </div>
        </div>
    );
};

const InsightStudio = () => {
    const clientId = getClientId();
    const hasPermission = hasProductPermission('insight_studio');

    // The picked wall-clock IS the UTC value -- "12:24:25" is sent as
    // "12:24:25Z", not shifted by the browser's timezone. Built by string, not
    // via Date, so no local offset is ever applied. datetime-local omits
    // seconds when they are zero, hence the pad.
    // datetime-local's format, in UTC, since the field is UTC.
    const nowUtcValue = () => new Date().toISOString().slice(0, 19);
    const [endDateLocal, setEndDateLocal] = useState(nowUtcValue);
    const toUtcIso = (value) => {
        if (!value) return '';
        const [date, time = ''] = value.split('T');
        if (!date || !time) return '';
        const parts = time.split(':');
        while (parts.length < 3) parts.push('00');
        return `${date}T${parts.slice(0, 3).map((n) => n.padStart(2, '0')).join(':')}Z`;
    };
    const endDateUtc = toUtcIso(endDateLocal);
    // The window cannot end in the future, so the picker is capped at now.
    const nowUtcMax = nowUtcValue();

    const [projects, setProjects] = useState([]);
    const [topics, setTopics] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');
    const [selectedTopic, setSelectedTopic] = useState('');

    const [defaultPrompts, setDefaultPrompts] = useState([]);
    const [selectedPromptId, setSelectedPromptId] = useState('');

    const [conversations, setConversations] = useState([]);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [messages, setMessages] = useState([]);

    const [prompt, setPrompt] = useState('');
    const [error, setError] = useState('');

    const [loadingProjects, setLoadingProjects] = useState(false);
    const [loadingTopics, setLoadingTopics] = useState(false);
    const [loadingConversations, setLoadingConversations] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    // Gates the send button and drives the typing indicator. Keep every await
    // that a send depends on inside handleSend's try, so this clears exactly
    // when the answer reaches the screen -- an extra round-trip after the
    // answer was appended once left the dots showing underneath it.
    const [sending, setSending] = useState(false);

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    // Message whose metadata is shown in the right-hand details drawer (null = closed).
    const [detailsMessage, setDetailsMessage] = useState(null);
    const [visibleTweetCount, setVisibleTweetCount] = useState(TWEET_PAGE_SIZE);

    // Each response opens on its first page, however far the last one was read.
    useEffect(() => {
        setVisibleTweetCount(TWEET_PAGE_SIZE);
    }, [detailsMessage]);

    useEffect(() => {
        if (!detailsMessage) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setDetailsMessage(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [detailsMessage]);

    // StrictMode mounts, unmounts and remounts in development, so an effect
    // with no dependencies still runs twice -- which showed up upstream as two
    // of every load request. Refs survive that remount, so this runs the
    // initial load once, as it does in production.
    const bootstrapped = useRef(false);

    useEffect(() => {
        if (!hasPermission) return;
        if (!clientId) {
            setError('No logged-in email found. Please log in again.');
            return;
        }
        if (bootstrapped.current) return;
        bootstrapped.current = true;

        fetchProjects();
        fetchConversations();
        fetchDefaultPrompts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        // Collapse, measure, then size in the same pass. Routing the result
        // through state meant an unchanged height skipped the re-render, so the
        // "auto" set here stayed on the element and the box dropped to one row
        // the moment a long prompt was edited.
        el.style.height = 'auto';
        const next = Math.min(Math.max(el.scrollHeight, 42), 160);
        el.style.height = `${next}px`;
        el.style.overflowY = next >= 160 ? 'auto' : 'hidden';
    }, [prompt]);

    const fetchProjects = async (endDate = endDateUtc) => {
        setLoadingProjects(true);
        try {
            const query = endDate ? `?end_date=${encodeURIComponent(endDate)}` : '';
            const response = await fetch(`/api/insight-studio/projects${query}`, NO_STORE);
            if (!response.ok) throw new Error('Unable to load projects');
            const data = await response.json();
            const list = Array.isArray(data) ? data : [];
            list.sort((a, b) => a.project_name.localeCompare(b.project_name));
            setProjects(list);
        } catch (err) {
            setError(err.message || 'Unable to load projects');
        } finally {
            setLoadingProjects(false);
        }
    };

    const fetchTopics = async (projectId, endDate = endDateUtc) => {
        setLoadingTopics(true);
        try {
            const query = endDate ? `&end_date=${encodeURIComponent(endDate)}` : '';
            const response = await fetch(
                `/api/insight-studio/topics?project_id=${projectId}${query}`,
                NO_STORE
            );
            if (!response.ok) throw new Error('Unable to load topics');
            const data = await response.json();
            const list = Array.isArray(data) ? data : [];
            list.sort((a, b) => a.topic_name.localeCompare(b.topic_name));
            setTopics(list);
        } catch (err) {
            setError(err.message || 'Unable to load topics');
        } finally {
            setLoadingTopics(false);
        }
    };

    const fetchConversations = async () => {
        setLoadingConversations(true);
        try {
            const response = await fetch(`/api/insight-studio/conversations?client_id=${encodeURIComponent(clientId)}`, NO_STORE);
            if (!response.ok) throw new Error('Unable to load conversations');
            const data = await response.json();
            setConversations(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || 'Unable to load conversations');
        } finally {
            setLoadingConversations(false);
        }
    };

    const fetchMessages = async (conversationId) => {
        setLoadingMessages(true);
        try {
            const response = await fetch(`/api/insight-studio/conversations/${conversationId}/messages`, NO_STORE);
            if (!response.ok) throw new Error('Unable to load conversation history');
            const data = await response.json();
            setMessages(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || 'Unable to load conversation history');
        } finally {
            setLoadingMessages(false);
        }
    };

    /**
     * Suggested starter prompts for the sidebar.
     *
     * Older InsightsStudio builds have no /default-prompts route, so a failure
     * here is not surfaced as an error -- the section just stays hidden. The
     * shape is normalised because entries may be plain strings or objects.
     */
    const fetchDefaultPrompts = async () => {
        // The API is the only source. Anything less than a usable list leaves
        // defaultPrompts empty, which hides the dropdown entirely.
        try {
            // Deliberately unfiltered: passing project_id makes the API return
            // only prompts scoped to that project, excluding the global ones
            // (project_id null) that should always be offered. The whole list is
            // fetched once and narrowed by visibleDefaultPrompts instead.
            const response = await fetch('/api/insight-studio/default-prompts', NO_STORE);
            if (!response.ok) {
                setDefaultPrompts([]);
                return;
            }
            const data = await response.json();
            const list = Array.isArray(data) ? data : [];
            setDefaultPrompts(
                list
                    .filter((item) => item && typeof item.prompt === 'string' && item.prompt.trim() !== '')
                    .map((item) => ({
                        id: item.default_prompt_id,
                        // `label` is the short name shown in the dropdown; `prompt`
                        // is the full instruction text, often hundreds of characters.
                        label: (item.label || '').trim() || `Prompt ${item.default_prompt_id}`,
                        prompt: item.prompt,
                        // null = available for every project; otherwise scoped to one.
                        projectId: item.project_id ?? null
                    }))
            );
        } catch {
            setDefaultPrompts([]);
        }
    };

    const applyDefaultPrompt = (text) => {
        setPrompt(text);
        textareaRef.current?.focus();
    };

    // Global prompts (project_id null) always apply; the rest only for their project.
    const visibleDefaultPrompts = defaultPrompts.filter(
        (p) => p.projectId === null || String(p.projectId) === String(selectedProject)
    );

    // Only record the value here. datetime-local fires change for each segment
    // as it is filled in, so reloading the lists from this handler meant an API
    // call per keystroke and a re-render of the field while the native picker
    // was still open -- which is what kept the calendar from closing.
    const handleEndDateChange = (e) => setEndDateLocal(e.target.value);

    // The window the lists currently describe. Comparing against the value
    // rather than counting passes is what makes the remount above a no-op: a
    // "have I run before" flag was already true on the second pass, so it
    // reloaded projects for a window that had not changed.
    const loadedEndDate = useRef(endDateUtc);

    useEffect(() => {
        if (loadedEndDate.current === endDateUtc) return;

        // Wait for the value to settle. datetime-local fires change for each
        // segment as it is filled in, so reloading per keystroke would re-render
        // the field while the native picker is still open and keep it from
        // closing.
        const timer = setTimeout(() => {
            loadedEndDate.current = endDateUtc;
            // Both selections belong to the moment they were made -- the topic
            // counts differ window to window, and a project can be absent from
            // one entirely -- so start over rather than carry a stale choice
            // across. Projects reload for the new window here; topics follow
            // once a project is picked again.
            setSelectedProject('');
            setSelectedTopic('');
            setSelectedPromptId('');
            setTopics([]);
            fetchProjects(endDateUtc);
        }, 600);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endDateUtc]);

    const handleProjectChange = (e) => {
        const projectId = e.target.value;
        setSelectedProject(projectId);
        setSelectedTopic('');
        setSelectedPromptId('');
        setTopics([]);
        if (projectId) {
            fetchTopics(projectId);
        }
    };

    const handleNewChat = () => {
        setActiveConversationId(null);
        setMessages([]);
        setSelectedProject('');
        setSelectedTopic('');
        setSelectedPromptId('');
        setEndDateLocal(nowUtcValue());
        setTopics([]);
        setPrompt('');
        setError('');
    };

    const handleClearChat = () => {
        setActiveConversationId(null);
        setMessages([]);
        // Every dropdown goes back to its placeholder, along with the message
        // box, so nothing is left showing a selection the cleared chat no
        // longer has.
        setSelectedProject('');
        setSelectedTopic('');
        setSelectedPromptId('');
        setEndDateLocal(nowUtcValue());
        setTopics([]);
        setPrompt('');
        setError('');
    };

    const handleSelectConversation = (conversationId) => {
        if (conversationId === activeConversationId) return;
        setActiveConversationId(conversationId);
        setError('');
        setSelectedProject('');
        setSelectedTopic('');
        setTopics([]);
        fetchMessages(conversationId);
    };

    const handleSend = async () => {
        const trimmedPrompt = prompt.trim();

        if (!effectiveProjectId || !effectiveTopicId) {
            setError('Please select a project and topic before asking a question.');
            return;
        }
        if (!trimmedPrompt) return;

        setSending(true);
        setError('');

        const optimisticMessage = {
            message_id: `pending-${messages.length}`,
            role: 'user',
            content: trimmedPrompt,
            project_id: effectiveProjectId,
            topic_id: effectiveTopicId,
            project_name: projects.find((p) => String(p.project_id) === String(effectiveProjectId))?.project_name,
            topic_name: topics.find((t) => String(t.topic_id) === String(effectiveTopicId))?.topic_name,
            end_date: endDateUtc || null,
            created_at: new Date().toISOString()
        };
        setMessages((prev) => [...prev, optimisticMessage]);
        setPrompt('');

        const askBody = {
            client_id: clientId,
            conversation_id: activeConversationId || null,
            project_id: Number(effectiveProjectId),
            topic_id: Number(effectiveTopicId),
            prompt: trimmedPrompt,
            // The GETs must omit this (the API 422s on an empty value),
            // but the JSON body takes a real null.
            end_date: endDateUtc || null
        };

        logExchange('out', 'POST /api/insight-studio/ask', askBody);
        const startedAt = performance.now();

        try {
            const response = await fetch('/api/insight-studio/ask', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(askBody)
            });

            const elapsedMs = Math.round(performance.now() - startedAt);

            if (!response.ok) {
                const data = await response.json().catch(() => null);
                logExchange('error', `${response.status} from /ask in ${elapsedMs}ms`, data);
                throw new Error(data?.message || 'Unable to send prompt');
            }

            const data = await response.json();
            logExchange('in', `answer in ${elapsedMs}ms`, data);
            const isNewConversation = !activeConversationId;

            setMessages((prev) => [
                ...prev,
                {
                    message_id: `answer-${data.conversation_id}-${prev.length}`,
                    role: 'assistant',
                    content: data.answer,
                    // /ask returns the tweets the answer was built from, so the
                    // MetaData drawer works on a just-sent answer without
                    // re-reading the conversation. The real message_id only
                    // arrives when the conversation is next opened, and nothing
                    // on screen needs it before then.
                    tweets: Array.isArray(data.tweets) ? data.tweets : [],
                    end_date: data.end_date || null,
                    created_at: new Date().toISOString()
                }
            ]);

            if (isNewConversation) {
                setActiveConversationId(data.conversation_id);
                fetchConversations();
            }
        } catch (err) {
            logExchange('error', 'send failed', err);
            setError(err.message || 'Unable to send prompt');
            setMessages((prev) => prev.filter((m) => m.message_id !== optimisticMessage.message_id));
            setPrompt(trimmedPrompt);
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // /ask needs both ids, so the prompt picker, the composer and the send
    // button all gate on this -- three separate conditions had drifted apart,
    // letting a prompt be chosen that then filled a disabled box.
    /**
     * The project and topic an open conversation was last held in.
     *
     * Opening one from history deliberately leaves the dropdowns empty, but a
     * follow-up still has to reach /ask with ids. Search backwards for the most
     * recent message carrying them -- follow-ups are stored with neither, so
     * this lands on the exchange that actually ran the retrieval.
     */
    const conversationContext = [...messages].reverse().find((m) => m.project_id && m.topic_id) || null;

    // An explicit choice always wins; otherwise fall back to the conversation's.
    const effectiveProjectId = selectedProject || conversationContext?.project_id || '';
    const effectiveTopicId = selectedTopic || conversationContext?.topic_id || '';

    const hasContext = Boolean(effectiveProjectId && effectiveTopicId);

    // The backend records project and topic on a conversation's first exchange
    // only, so the tags belong on that message alone. The optimistic message
    // built when sending always carries the current selection, which would
    // otherwise label every follow-up too.
    const firstUserMessageId = messages.find((m) => m.role === 'user')?.message_id;
    const canSend = Boolean(hasContext && prompt.trim() && !sending);

    // Every tweet this response retrieved, in the order the API returned them,
    // with verbatim reposts collapsed into one card that records how many it
    // stands for. Follow-up answers are stored with tweets [] and hide the
    // MetaData button entirely, so this can only ever be one response's own.
    const detailsTweets = dedupeTweets(detailsMessage?.tweets);
    const visibleTweets = detailsTweets.slice(0, visibleTweetCount);
    const remainingTweets = detailsTweets.length - visibleTweets.length;

    if (!hasPermission) {
        return (
            <div className="wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
                <Header />
                <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#15171c' }}>
                    <div style={{
                        color: '#fff',
                        backgroundColor: '#2a1414',
                        border: '1px solid #c0392b',
                        padding: '18px 26px',
                        borderRadius: '10px',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '15px'
                    }}>
                        Access Denied: Insight Studio permission required.
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', backgroundColor: '#15171c' }}>
            <Header />
            <div style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                backgroundColor: '#15171c',
                overflow: 'hidden'
            }}>
                {/* Sidebar: context + history */}
                <div style={{
                    width: '270px',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                    borderRight: '1px solid #2a2a2a',
                    backgroundColor: '#1a1a1a',
                    padding: '16px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0
                }}>
                    <button
                        onClick={fetchProjects}
                        disabled={loadingProjects}
                        style={{ ...sidebarButtonStyle, marginBottom: '12px' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#333')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2a2a2a')}
                    >
                        <FontAwesomeIcon icon={faArrowsRotate} spin={loadingProjects} />
                        Refresh Projects
                    </button>

                    <label style={{ color: '#8a8f98', fontSize: '11px', marginBottom: '5px', display: 'block', fontWeight: 600 }}>
                        Point in time (UTC)
                    </label>
                    <input
                        type="datetime-local"
                        step="1"
                        className="insight-select"
                        value={endDateLocal}
                        onChange={handleEndDateChange}
                        max={nowUtcMax}
                        style={{ ...selectStyle, marginBottom: '12px', colorScheme: 'dark' }}
                    />


                    <label style={{ color: '#8a8f98', fontSize: '11px', marginBottom: '5px', display: 'block', fontWeight: 600 }}>Project</label>
                    <select
                        className="insight-select"
                        value={selectedProject}
                        onChange={handleProjectChange}
                        disabled={loadingProjects}
                        style={{ ...selectStyle, marginBottom: '12px' }}
                    >
                        <option value="">
                            {loadingProjects ? 'Loading projects...' : '— select project —'}
                        </option>
                        {projects.map((project) => (
                            <option key={project.project_id} value={project.project_id}>
                                {project.project_name}
                            </option>
                        ))}
                    </select>

                    <label style={{ color: '#8a8f98', fontSize: '11px', marginBottom: '5px', display: 'block', fontWeight: 600 }}>Topic</label>
                    <div style={{ marginBottom: '14px' }}>
                        <SearchableSelect
                            options={topics}
                            value={selectedTopic}
                            onChange={setSelectedTopic}
                            getKey={(topic) => topic.topic_id}
                            getLabel={(topic) => topic.topic_name}
                            disabled={!selectedProject || loadingTopics}
                            placeholder={loadingTopics ? 'Loading topics...' : (selectedProject ? 'Search topics...' : 'Select a project first')}
                        />
                    </div>

                    {/* Starter prompts from GET /default-prompts. The whole block is
                        hidden when the API returns nothing usable. */}
                    {visibleDefaultPrompts.length > 0 && (
                        <div style={{ marginBottom: '14px' }}>
                            <label style={{ color: '#8a8f98', fontSize: '11px', marginBottom: '5px', display: 'block', fontWeight: 600 }}>
                                Default prompts
                            </label>
                            <select
                                className="insight-select"
                                value={selectedPromptId}
                                // Always available: a prompt can be picked first and the
                                // project and topic chosen afterwards. Only the composer
                                // below needs both, since /ask requires their ids.
                                onChange={(e) => {
                                    const id = e.target.value;
                                    setSelectedPromptId(id);
                                    const chosen = visibleDefaultPrompts.find((p) => String(p.id) === String(id));
                                    if (chosen) applyDefaultPrompt(chosen.prompt);
                                }}
                                // The full prompt is long, so it lives in the tooltip.
                                title={
                                    visibleDefaultPrompts.find((p) => String(p.id) === String(selectedPromptId))?.prompt ||
                                    'Pick a prompt to load it into the message box'
                                }
                                style={{ ...selectStyle, cursor: 'pointer' }}
                            >
                                <option value="">
                                    — select a prompt —
                                </option>
                                {visibleDefaultPrompts.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div style={{ borderTop: '1px solid #2a2a2a', margin: '2px 0 14px' }} />

                    <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
                        <button
                            onClick={handleNewChat}
                            style={{ ...sidebarButtonStyle, flex: 1, backgroundColor: '#2b97f0', border: 'none' }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1e7ac7')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2b97f0')}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            New chat
                        </button>
                        <button
                            onClick={handleClearChat}
                            style={{ ...sidebarButtonStyle, flex: 1 }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#333')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2a2a2a')}
                        >
                            <FontAwesomeIcon icon={faBroom} />
                            Clear chat
                        </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', color: '#fff', fontWeight: '700', fontSize: '13px', marginBottom: '8px', letterSpacing: '0.2px' }}>
                        <FontAwesomeIcon icon={faComments} style={{ opacity: 0.6, fontSize: '12px' }} />
                        HISTORY
                    </div>

                    <div className="insight-scroll" style={{ overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '2px' }}>
                        {loadingConversations ? (
                            <div style={{ color: '#888', fontSize: '12.5px' }}>Loading...</div>
                        ) : conversations.length === 0 ? (
                            <div style={{ color: '#888', fontSize: '12.5px' }}>No conversations yet.</div>
                        ) : (
                            conversations.map((conversation) => {
                                const isActive = conversation.conversation_id === activeConversationId;
                                return (
                                    <button
                                        key={conversation.conversation_id}
                                        onClick={() => handleSelectConversation(conversation.conversation_id)}
                                        style={{
                                            textAlign: 'left',
                                            padding: '9px 10px',
                                            borderRadius: '6px',
                                            border: isActive ? '1px solid #2b97f0' : '1px solid transparent',
                                            backgroundColor: isActive ? 'rgba(43, 151, 240, 0.15)' : 'transparent',
                                            color: '#e0e0e0',
                                            cursor: 'pointer',
                                            fontSize: '12.5px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                            transition: 'background-color 0.15s ease'
                                        }}
                                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#242424'; }}
                                        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                    >
                                        {formatEastern(conversation.created_at)}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Main chat panel */}
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ flexShrink: 0, padding: '14px 24px', borderBottom: '1px solid #2a2a2a' }}>
                        <h1 style={{ color: '#fff', margin: 0, fontSize: '17px', fontWeight: '700' }}>Insight Studio</h1>
                        <p style={{ color: '#8a8f98', margin: '2px 0 0', fontSize: '12px' }}>
                            AI-driven social sentiment analysis, powered by Social Market Analytics.
                        </p>
                    </div>

                    {error && (
                        <div style={{
                            flexShrink: 0,
                            color: 'white',
                            backgroundColor: '#c0392b',
                            padding: '10px 14px',
                            borderRadius: '6px',
                            margin: '12px 24px 0',
                            fontSize: '13px'
                        }}>
                            {error}
                        </div>
                    )}

                    <div className="insight-scroll" style={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        padding: '20px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                    }}>
                        {loadingMessages ? (
                            <div style={{ color: '#888', textAlign: 'center', marginTop: '40px', fontSize: '13px' }}>Loading conversation...</div>
                        ) : messages.length === 0 ? (
                            <div style={{ color: '#888', textAlign: 'center', marginTop: '40px', fontSize: '13px' }}>
                                Select a project and topic in the sidebar, then ask a question to start a new conversation.
                            </div>
                        ) : (
                            messages.map((message) => (
                                <div key={message.message_id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flexDirection: message.role === 'user' ? 'row-reverse' : 'row' }}>
                                    <div style={{
                                        flexShrink: 0,
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: message.role === 'user' ? '#2b97f0' : '#4a4a4a',
                                        color: '#fff',
                                        fontSize: '12px'
                                    }}>
                                        <FontAwesomeIcon icon={message.role === 'user' ? faUser : faRobot} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: message.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '75%', minWidth: 0 }}>
                                        {message.message_id === firstUserMessageId &&
                                            (message.project_name || message.topic_name || message.end_date) && (
                                            <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                {message.project_name && <Tag icon={faLayerGroup}>{message.project_name}</Tag>}
                                                {message.topic_name && <Tag icon={faTag}>{message.topic_name}</Tag>}
                                                {/* The moment the answer was generated for, as sent to /ask. */}
                                                {message.end_date && <Tag icon={faClock}>{message.end_date}</Tag>}
                                            </div>
                                        )}
                                        <div style={{
                                            color: message.role === 'user' ? '#fff' : '#f0f0f0',
                                            backgroundColor: message.role === 'user' ? '#2b97f0' : '#232323',
                                            fontSize: '13.5px',
                                            lineHeight: '1.55',
                                            whiteSpace: 'pre-wrap',
                                            padding: '9px 13px',
                                            borderRadius: '13px',
                                            borderBottomRightRadius: message.role === 'user' ? '3px' : '13px',
                                            borderBottomLeftRadius: message.role === 'user' ? '13px' : '3px'
                                        }}>
                                            {message.content}
                                        </div>
                                        {/* Only where there is something to show: the drawer's
                                            tweets are the response's own, so an answer that
                                            retrieved none has nothing to open. */}
                                        {message.role !== 'user' &&
                                            Array.isArray(message.tweets) &&
                                            message.tweets.length > 0 && (
                                            <button
                                                type="button"
                                                className="insight-meta-btn"
                                                onClick={() => setDetailsMessage(message)}
                                                title="View response details"
                                                style={{
                                                    alignSelf: 'flex-end',
                                                    marginTop: '4px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '5px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    padding: '2px 2px',
                                                    cursor: 'pointer',
                                                    color: '#7b7b7b',
                                                    fontSize: '10.5px',
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                <span>MetaData</span>
                                                <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: '10px' }} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                        {sending && (
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <div style={{
                                    flexShrink: 0, width: '28px', height: '28px', borderRadius: '50%',
                                    backgroundColor: '#4a4a4a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px'
                                }}>
                                    <FontAwesomeIcon icon={faRobot} />
                                </div>
                                <div style={{ backgroundColor: '#232323', borderRadius: '13px', borderBottomLeftRadius: '3px', padding: '11px 14px', display: 'flex', gap: '4px' }}>
                                    <span className="insight-typing-dot" />
                                    <span className="insight-typing-dot" />
                                    <span className="insight-typing-dot" />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div style={{ flexShrink: 0, display: 'flex', gap: '10px', alignItems: 'flex-end', padding: '12px 24px 16px', borderTop: '1px solid #2a2a2a' }}>
                        <textarea
                            ref={textareaRef}
                            className="insight-textarea"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onKeyDown={handleKeyDown}
                            // Always typeable: a question can be written, or a default
                            // prompt loaded and edited, before a project and topic are
                            // chosen. Only sending needs them, since /ask takes their ids.
                            placeholder={hasContext
                                ? 'Ask something about the selected topic...'
                                : 'Type a question — select a project and topic to send'}
                            rows={1}
                            style={{
                                flex: 1,
                                minHeight: '42px',
                                maxHeight: '160px',
                                padding: '10px 14px',
                                backgroundColor: '#232323',
                                color: '#fff',
                                border: '1px solid #3d3d3d',
                                borderRadius: '12px',
                                fontSize: '13.5px',
                                lineHeight: '1.4',
                                resize: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                        <button
                            className="insight-send-btn"
                            onClick={handleSend}
                            disabled={!canSend}
                            style={{
                                flexShrink: 0,
                                width: '38px',
                                height: '38px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: canSend ? '#2b97f0' : '#333',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '50%',
                                cursor: canSend ? 'pointer' : 'not-allowed',
                                fontSize: '13px',
                                transition: 'background-color 0.15s ease'
                            }}
                        >
                            <FontAwesomeIcon icon={faPaperPlane} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Right-hand details drawer for a response's metadata. */}
            {detailsMessage && (
                <>
                    <div
                        onClick={() => setDetailsMessage(null)}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.45)',
                            zIndex: 1000
                        }}
                    />
                    <div
                        className="insight-drawer"
                        role="dialog"
                        aria-label="Tweets"
                        style={{
                            position: 'fixed',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: 'min(420px, 92vw)',
                            backgroundColor: '#1b1d22',
                            borderLeft: '1px solid #2a2a2a',
                            boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.35)',
                            zIndex: 1001,
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <div style={{
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '14px 16px',
                            borderBottom: '1px solid #2a2a2a'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', fontWeight: 600, fontSize: '14px' }}>
                                <FontAwesomeIcon icon={faCircleInfo} style={{ opacity: 0.7 }} />
                                Tweets
                            </div>
                            <button
                                type="button"
                                onClick={() => setDetailsMessage(null)}
                                title="Close"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#aaa',
                                    cursor: 'pointer',
                                    fontSize: '15px',
                                    padding: '4px 6px'
                                }}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>

                        <div className="insight-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px' }}>
                            {detailsMessage.created_at && (
                                <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
                                    <Tag icon={faClock}>{formatEastern(detailsMessage.created_at)}</Tag>
                                </div>
                            )}


                            <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '7px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <XLogo size={11} />
                                Source tweets
                                {detailsTweets.length > 0 && (
                                    <span style={{ color: '#666' }}>({detailsTweets.length})</span>
                                )}
                            </div>
                            {detailsTweets.length > 0 ? (
                                <div style={{ marginBottom: '18px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {visibleTweets.map((row, i) => (
                                            <TweetCard
                                                key={row.tweet.constructed_url || i}
                                                tweet={row.tweet}
                                                duplicates={row.duplicates}
                                            />
                                        ))}
                                    </div>
                                    {remainingTweets > 0 && (
                                        <button
                                            type="button"
                                            className="insight-load-more"
                                            onClick={() =>
                                                setVisibleTweetCount((n) => n + TWEET_PAGE_SIZE)
                                            }
                                        >
                                            Load more
                                            <span style={{ color: '#777' }}>
                                                ({remainingTweets} left)
                                            </span>
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div style={{ color: '#666', fontSize: '12px', marginBottom: '18px' }}>
                                    No source tweets could be linked to this response.
                                </div>
                            )}

                        </div>
                    </div>
                </>
            )}

            <Footer />
        </div>
    );
};

export default InsightStudio;
